// Full BuildingPage with responsive + mobile-first scrollable layout
import React, { useState, useEffect, useRef } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Check, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { useToast } from './ui/use-toast';

const SpaceUnit = ({ space, isSelected, hasTour, hasChecklist, onClick, onDelete, canDelete, spacing }) => {
  const { spaceWidth, spaceHeight } = spacing;
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <g
          onClick={() => onClick(space.id)}
          className="cursor-pointer transition-all hover:opacity-90"
        >
          <rect
            width={spaceWidth}
            height={spaceHeight}
            fill={isSelected ? '#4338ca' : '#fff'}
            stroke={isSelected ? '#4338ca' : '#d1d5db'}
            strokeWidth={isSelected ? 3 : 1.5}
            rx={10}
            ry={10}
          />
          <text
            x={spaceWidth / 2}
            y={spaceHeight / 2}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={12}
            fill={isSelected ? '#fff' : '#000'}
            fontWeight={isSelected ? 600 : 500}
          >
            {space.name}
          </text>
          {canDelete && (
            <g
              onClick={e => {
                e.stopPropagation();
                onDelete(space.id);
              }}
            >
              <Trash2 x={spaceWidth - 14} y={spaceHeight - 11} className="text-red-500 w-3 h-3" />
            </g>
          )}
          {hasTour && <Check x={4} y={4} className="text-green-600 w-3 h-3 z-100" />}
          {hasChecklist && <Check x={spaceWidth - 14} y={canDelete ? 16 : 4} className="text-blue-600 w-3 h-3 z-100" />}
        </g>
      </Tooltip.Trigger>
      <Tooltip.Content side="top">
        <div className="bg-black text-white px-2 py-1 text-xs rounded">דירה</div>
      </Tooltip.Content>
    </Tooltip.Root>
  );
};

const FloorRow = ({
  floor,
  spaces,
  selectedSpace,
  onSpaceClick,
  tourMap,
  checklistMap,
  onDeleteSpace,
  spacing,
  canDelete,
}) => {
  const floorWidth = spaces.length * (spacing.spaceWidth + spacing.spaceGap);
  const spaceElements = spaces.map((space, i) => (
    <g key={space.id} transform={`translate(${i * (spacing.spaceWidth + spacing.spaceGap)}, 0)`}>
      <SpaceUnit
        space={space}
        isSelected={selectedSpace === space.id}
        hasTour={tourMap?.[space.id]}
        hasChecklist={checklistMap?.[space.id]}
        onClick={onSpaceClick}
        onDelete={onDeleteSpace}
        canDelete={canDelete}
        spacing={spacing}
      />
    </g>
  ));

  return (
    <g transform={`translate(0, ${floor.offsetY})`}>
      <text x={10} y={spacing.spaceHeight / 2 + 5} fontWeight="bold" fontSize={14}>
        {floor.name}
      </text>
      <g transform={`translate(100, 0)`}>{spaceElements}</g>
    </g>
  );
};

export function BuildingSVG({
  floors,
  spaces,
  selectedSpace,
  onSpaceClick,
  tourMap,
  checklistMap,
  onDeleteSpace,
  canDeleteSpace,
}) {
  const containerRef = useRef(null);
  const [zoom, setZoom] = useState(1);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const baseZoom = isMobile ? 0.7 : 1;
  const spacing = {
    spaceWidth: 80 * zoom * baseZoom,
    spaceHeight: 36 * zoom * baseZoom,
    spaceGap: 10 * zoom * baseZoom,
  };

  const { toast } = useToast();

  const reversedFloors = [...floors].sort((a, b) => {
    const extractNumber = name => {
      const normalized = name.replace(/(\d+)-$/, '-$1');
      const match = normalized.match(/-?\d+/);
      return match ? parseInt(match[0], 10) : 0;
    };
    return extractNumber(b.name) - extractNumber(a.name);
  });
  const floorHeight = spacing.spaceHeight + 50;

  const maxSpaces = Math.max(
    ...reversedFloors.map(floor => spaces.filter(s => s.floorId === floor.id).length)
  );
  const svgWidth = maxSpaces * (spacing.spaceWidth + spacing.spaceGap) + 150;
  const svgHeight = reversedFloors.length * floorHeight + 60;

  return (
    <div className="p-2 sm:p-4 w-full h-full flex flex-col gap-4">
      <div className="flex items-center gap-4 px-2 sm:px-0 overflow-x-auto">
        <Button onClick={() => setZoom(z => Math.max(0.5, z - 0.1))}>-</Button>
        <Slider
          className="w-40"
          value={[zoom * 100]}
          onValueChange={v => setZoom(v[0] / 100)}
          min={50}
          max={200}
          step={10}
        />
        <Button onClick={() => setZoom(z => Math.min(2, z + 0.1))}>+</Button>
      </div>

      <div
        ref={containerRef}
        className="overflow-auto w-full h-[80vh] border rounded shadow-sm bg-background"
      >
        <svg
          className="bg-white"
          width={svgWidth}
          height={svgHeight}
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        >
          {reversedFloors.map((floor, i) => (
            <FloorRow
              key={floor.id}
              floor={{ ...floor, offsetY: i * floorHeight }}
              spaces={spaces.filter(s => s.floorId === floor.id)}
              selectedSpace={selectedSpace}
              onSpaceClick={onSpaceClick}
              onDeleteSpace={onDeleteSpace}
              tourMap={tourMap}
              checklistMap={checklistMap}
              spacing={spacing}
              canDelete={canDeleteSpace}
            />
          ))}
        </svg>
      </div>
    </div>
  );
}

export default function BuildingPage(props) {
  return <BuildingSVG {...props} />;
}
